import { IEquipment } from '@frontend/equipment/types';
import { ScreenTrigger } from '@frontend/wizard/types';
import { useCallback, useEffect, useMemo } from 'react';
import { useLocation } from 'react-router-dom';

import { useKitForProduct } from '../kit-return/shared/use-kit-for-product';
import { useScreen } from '../useScreen';

export const useKitReturnMethod = () => {
    const location = useLocation();
    const { store, screen_type, updateWizardData, onBack, onSubmit } = useScreen();
    const locationEquipment = (location.state as { equipment?: IEquipment } | null)?.equipment ?? null;
    const wizardEquipment = store?.data?.equipment as IEquipment | undefined;
    const equipment = locationEquipment ?? wizardEquipment ?? null;

    const { kitId, entries, isLoading, loadError, retryLoad } = useKitForProduct(equipment?.product_id, equipment);

    const hasReturnableKit = !!kitId && entries.length > 0;
    const singleReturnStep = useMemo(
        () => store?.steps.find((step) => step.screen === screen_type && step.trigger === ScreenTrigger.SUBMIT_FALSE),
        [screen_type, store?.steps]
    );

    const persistEquipment = useCallback(() => {
        if (!equipment) return;
        updateWizardData({ equipment });
    }, [equipment, updateWizardData]);

    const onReturnKit = useCallback(() => {
        if (!hasReturnableKit) return;
        persistEquipment();
        onSubmit();
    }, [hasReturnableKit, onSubmit, persistEquipment]);

    const onReturnSingle = useCallback(() => {
        if (!singleReturnStep) return;
        persistEquipment();
        onSubmit(singleReturnStep);
    }, [onSubmit, persistEquipment, singleReturnStep]);

    useEffect(() => {
        if (!equipment || isLoading || loadError || hasReturnableKit || !singleReturnStep) return;
        onReturnSingle();
    }, [equipment, hasReturnableKit, isLoading, loadError, onReturnSingle, singleReturnStep]);

    return {
        equipment,
        entries,
        hasReturnableKit,
        isLoading,
        loadError,
        retryLoad,
        singleReturnAvailable: !!singleReturnStep,
        onBack,
        onReturnKit,
        onReturnSingle
    };
};
